import { useState, useCallback, useRef, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { StripeTerminal, TerminalConnectTypes, TerminalEventsEnum } from '@capacitor-community/stripe-terminal';
import { paymentService } from '../../../_services/payment.service';

/**
 * Hook for Tap to Pay on iPhone via @capacitor-community/stripe-terminal.
 *
 * Uses the native iOS Stripe Terminal SDK (NOT the web @stripe/terminal-js
 * SDK that powers Beu Card Reader). Tap to Pay needs the native bridge
 * because the iPhone's Secure Element / NFC chip is only accessible from
 * the native iOS Stripe Terminal SDK with the proper Apple entitlement.
 *
 * Availability requirements:
 *   - Running inside the Capacitor iOS app (not web, not Android)
 *   - iOS 16.4 or later
 *   - iPhone XS or later (A12 Bionic chip with Secure Element)
 *   - Apple's "Tap to Pay on iPhone" entitlement granted to the app
 *
 * If any check fails, isAvailable === false and the FE should hide the
 * Tap to Pay tile entirely. Salons on iPad/Android/web/old iPhones get
 * a clean experience — they just won't see the option.
 *
 * Same API surface as useStripeTerminal so callers can drop it in:
 *   - isAvailable, isConnecting, isProcessing, connectedReader, error
 *   - connect(), processPayment({ clientSecret, paymentIntentId, onCapture, ... })
 *
 * The capture callback receives selectedPaymentType: 15 (Tap to Pay)
 * which the BE maps to the new TapToPay fee config (1.5% + 20p).
 */
export default function useTapToPayTerminal() {
  const initialisedRef = useRef(false);
  const connectedRef = useRef(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [connectedReader, setConnectedReader] = useState(null);
  const [error, setError] = useState(null);
  const [tipAmount, setTipAmount] = useState(0);

  // Statically imported above — dynamic imports failed under
  // capacitor://localhost in the WebView (no plugin chunk loaded, the
  // catch swallowed the error, isAvailable stayed false). Static import
  // is safe: the package only calls registerPlugin() on import; it
  // doesn't touch native APIs until the methods are invoked.
  const loadPlugin = useCallback(async () => {
    return { StripeTerminal, TerminalConnectTypes };
  }, []);

  // Detect device support on mount. We only flip isAvailable=true if all
  // gates pass — caller must always check before rendering UI.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const isNative = Capacitor.isNativePlatform();
        console.log('[TapToPay] isNative:', isNative);
        if (!isNative) return;

        const info = await Device.getInfo();
        console.log('[TapToPay] device info:', { platform: info.platform, osVersion: info.osVersion, model: info.model });
        if (info.platform !== 'ios') return;

        // iOS version check — Tap to Pay requires 16.4+
        const ver = parseFloat(info.osVersion || '0');
        if (ver < 16.4) {
          console.log('[TapToPay] iOS version too old:', ver);
          return;
        }

        // Mark available; connect() will surface entitlement/hardware
        // failures at use time. Plugin doesn't expose a pre-flight check.
        if (!cancelled) {
          console.log('[TapToPay] available — will show tile');
          setIsAvailable(true);
        }
      } catch (e) {
        console.error('[TapToPay] availability check failed:', e);
        if (!cancelled) setIsAvailable(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadPlugin]);

  // Cleanup on unmount: disconnect the reader if we're connected.
  useEffect(() => {
    return () => {
      (async () => {
        if (connectedRef.current) {
          try {
            const { StripeTerminal } = await loadPlugin();
            await StripeTerminal.disconnectReader();
          } catch (e) { /* ignore */ }
        }
      })();
    };
  }, [loadPlugin]);

  // Initialise the SDK with a connection-token provider. We reuse the
  // existing /payment/fetchConnectionToken backend endpoint that already
  // serves the Beu Card Reader flow — same Stripe Connect account, same
  // token shape.
  const initialiseSdk = useCallback(async () => {
    if (initialisedRef.current) return;
    const { StripeTerminal } = await loadPlugin();

    // Listen for connection token requests from the SDK and forward
    // them to our backend. The token must never be stored or logged.
    // The plugin's setConnectionToken accepts { token: string } only —
    // there is no error path; on failure we just don't call it and the
    // SDK times out after 60s with a clear error.
    StripeTerminal.addListener(TerminalEventsEnum.RequestedConnectionToken, async () => {
      console.log('[TapToPay] requestConnectionToken event received');
      try {
        const res = await paymentService.fetchConnectionToken();
        const token = res?.data?.responseData?.result?.secret;
        console.log('[TapToPay] fetchConnectionToken returned token?', !!token);
        if (!token) {
          console.error('[TapToPay] missing token in response:', res?.data);
          return;
        }
        await StripeTerminal.setConnectionToken({ token });
        console.log('[TapToPay] setConnectionToken delivered');
      } catch (e) {
        console.error('[TapToPay] fetchConnectionToken failed:', e?.response?.status, e?.response?.data || e?.message);
      }
    });

    await StripeTerminal.initialize({ isTest: false });
    initialisedRef.current = true;
  }, [loadPlugin]);

  // Connect to a Tap to Pay reader (the iPhone's own Secure Element).
  // The locationId argument is optional — if missing or stale, we fetch
  // a fresh one from the BE (which auto-creates a Stripe Terminal
  // Location for the stylist if one doesn't exist yet). This means
  // every stylist works out the box on first Tap to Pay use without
  // having to register a Beu Card Reader first.
  const connect = useCallback(async (locationId) => {
    setIsConnecting(true);
    setError(null);

    let resolvedLocationId = locationId;

    try {
      // Ensure we have a server-side Location. fetchConnectionToken's
      // BE handler runs _getConnectLocation under the hood which creates
      // a Location for the stylist if missing, and the response now
      // includes the resolved stripeLocationId.
      if (!resolvedLocationId) {
        console.log('[TapToPay] no locationId from prop — fetching from BE');
        const tokenRes = await paymentService.fetchConnectionToken();
        resolvedLocationId = tokenRes?.data?.responseData?.stripeLocationId;
        console.log('[TapToPay] BE returned stripeLocationId:', resolvedLocationId);
      }

      // Stripe Terminal requires a Location ID to associate the reader.
      // Without one the native plugin force-unwraps `self.locationId!`
      // in connectLocalMobileReader and crashes the app.
      if (!resolvedLocationId) {
        console.error('[TapToPay] could not resolve a stripeLocationId — aborting');
        setError('Could not set up Tap to Pay (no Stripe location available for this stylist).');
        setIsConnecting(false);
        return false;
      }

      await initialiseSdk();
      const { StripeTerminal, TerminalConnectTypes } = await loadPlugin();

      // If a previous attempt left the SDK with a live reader connection
      // (e.g. user cancelled mid-sheet), discoverReaders rejects with
      // Code=1110 "Already connected to a reader. Disconnect from the
      // reader, or power it off before trying again." Disconnect first
      // so the next discovery is clean.
      if (connectedRef.current) {
        console.log('[TapToPay] previous reader still connected — disconnecting before re-discover');
        try {
          await StripeTerminal.disconnectReader();
        } catch (e) {
          console.warn('[TapToPay] disconnect before reconnect failed (continuing):', e?.message);
        }
        connectedRef.current = false;
        setConnectedReader(null);
      }

      console.log('[TapToPay] discoverReaders with locationId:', resolvedLocationId);
      const { readers } = await StripeTerminal.discoverReaders({
        type: TerminalConnectTypes.TapToPay,
        locationId: resolvedLocationId,
      });

      if (!readers || readers.length === 0) {
        setError('Tap to Pay not available on this device');
        setIsConnecting(false);
        return false;
      }

      console.log('[TapToPay] connecting to reader:', readers[0]?.label || readers[0]?.serialNumber);
      await StripeTerminal.connectReader({ reader: readers[0] });
      connectedRef.current = true;
      setConnectedReader(readers[0]);
      setIsConnecting(false);
      return true;
    } catch (e) {
      setError(e?.message || 'Failed to connect Tap to Pay reader');
      setIsConnecting(false);
      return false;
    }
  }, [initialiseSdk, loadPlugin]);

  // Process a payment via Tap to Pay.
  // onCapture: optional async callback that captures the paymentIntent on
  // backend (typically dispatch(paymentActions.paymentIntentsCapture)).
  // selectedPaymentType is forced to 15 (TapToPay) so BE applies the
  // correct fee config.
  const processPayment = useCallback(async ({ clientSecret, paymentIntentId, bookingId, servicesToBook, voucherIds, onCapture }) => {
    if (!connectedRef.current) {
      setError('Tap to Pay reader not connected');
      return null;
    }

    setIsProcessing(true);
    setError(null);
    setTipAmount(0);

    try {
      const { StripeTerminal } = await loadPlugin();

      // Collect the customer's tap on the iPhone's Secure Element. This
      // surfaces the native "Hold card near top of phone" UI sheet.
      await StripeTerminal.collectPaymentMethod({ paymentIntent: clientSecret });

      // Confirm + process the payment intent.
      const result = await StripeTerminal.confirmPaymentIntent();

      // Capture on backend — flag it as Tap to Pay (selectedPaymentType: 15)
      // so the BE applies the TTP fee config and tags the transaction
      // correctly.
      const captureParams = {
        paymentIntentId,
        bookingId,
        servicesToBook,
        voucherIds: voucherIds || [],
        selectedPaymentType: 15,
      };
      const captureResponse = onCapture
        ? await onCapture(captureParams)
        : await paymentService.paymentIntentsCapture(captureParams);

      setIsProcessing(false);

      if (captureResponse?.data?.statusCode === 1) {
        const capturedTip = captureResponse.data?.responseData?.result?.tipAmount || 0;
        if (capturedTip > 0) setTipAmount(capturedTip);
        const capturedAmount = (captureResponse.data?.responseData?.result?.amount || 0) / 100;
        return { success: true, amount: capturedAmount, tipAmount: capturedTip, paymentIntent: result };
      }

      setError('Payment capture failed. Please try again.');
      return null;
    } catch (e) {
      setError(e?.message || 'Tap to Pay processing failed');
      setIsProcessing(false);
      return null;
    }
  }, [loadPlugin]);

  const disconnect = useCallback(async () => {
    if (!connectedRef.current) return;
    try {
      const { StripeTerminal } = await loadPlugin();
      await StripeTerminal.disconnectReader();
    } catch (e) { /* ignore */ }
    connectedRef.current = false;
    setConnectedReader(null);
  }, [loadPlugin]);

  return {
    isAvailable,
    isConnecting,
    isProcessing,
    connectedReader,
    error,
    tipAmount,
    connect,
    processPayment,
    disconnect,
  };
}
